// Curated mapping from Open5e monster identifiers to packIds defined in
// monsterPacks.js. Three tiers — slug (exact), keyword (regex on name),
// type (Open5e creature type). Resolution order is enforced in
// monsterPackKey() below. Extend this file freely; nothing breaks if a
// creature isn't covered — the renderer falls back to procedural sprites.

import { MONSTER_PACKS } from './monsterPacks';

export const PACK_BY_SLUG = {
  // Goblinoids
  'goblin': 'goblin_warrior',
  'goblin-boss': 'goblin_dual',
  'hobgoblin': 'goblin_spear',
  'hobgoblin-captain': 'goblin_dual',
  'bugbear': 'troll',
  'bugbear-chief': 'troll_battle',
  'kobold': 'goblin_warrior',
  // Undead
  'skeleton': 'skeleton',
  'zombie': 'skeleton',
  'ghoul': 'skeleton_thug',
  'ghast': 'ghost',
  'wight': 'skeleton_warlord',
  'wraith': 'ghost',
  'specter': 'ghost',
  'ghost': 'ghost',
  'mummy': 'skeleton_warlord',
  'lich': 'necromancer',
  'minotaur-skeleton': 'skeleton_warlord',
  // Beasts / animals
  'wolf': 'wolf_brown',
  'dire-wolf': 'wolf_raider',
  'worg': 'wolf_black',
  'brown-bear': 'bear',
  'black-bear': 'bear',
  'cave-bear': 'bear_mutant',
  'polar-bear': 'bear',
  'boar': 'boar',
  'giant-boar': 'boar',
  'warhorse': 'farm_horse_adult',
  'riding-horse': 'farm_horse_adult',
  'draft-horse': 'farm_horse_adult',
  'pony': 'farm_horse_foal',
  'mastiff': 'labrador',
  'cat': 'feline_bobcat',
  'panther': 'feline_mutated_panther',
  'lion': 'feline_mutated_panther',
  'tiger': 'feline_mutated_panther',
  'elk': 'deer_elk',
  'deer': 'deer',
  'giant-elk': 'deer_horned',
  'eagle': 'birds_eagle_bald',
  'giant-eagle': 'birds_phoenix',
  'owl': 'birds_owl_great_horned',
  'giant-owl': 'birds_owl_great_horned',
  'raven': 'birds_crow',
  'giant-vulture': 'birds_grackle',
  'giant-rat': 'dungeon_rat',
  'rat': 'dungeon_rat',
  'swarm-of-rats': 'dungeon_rat',
  'bat': 'dungeon_bat',
  'giant-bat': 'dungeon_bat_hoary',
  'swarm-of-bats': 'dungeon_bat',
  'giant-spider': 'spider_large',
  'spider': 'spider',
  'giant-wolf-spider': 'spider_albino',
  'swarm-of-insects': 'insect_fly',
  'giant-fly': 'insect_fly',
  'giant-firefly': 'insect_firefly',
  'giant-beetle': 'insect_beetle',
  'giant-dragonfly': 'insect_dragonfly',
  'killer-bee': 'bee',
  'giant-bee': 'bee_royal',
  'swarm-of-bees': 'bee_mini',
  'snake': 'snake_viper',
  'giant-poisonous-snake': 'snake_viper',
  'giant-constrictor-snake': 'snake_alien',
  'poisonous-snake': 'snake_viper',
  'octopus': 'pet_octopus',
  'giant-octopus': 'pet_octopus',
  'giant-shark': 'aquatic_shark_thresher',
  'hunter-shark': 'aquatic_shark_thresher',
  'reef-shark': 'aquatic_shark_thresher',
  'giant-sea-horse': 'pet_whale',
  'killer-whale': 'pet_whale',
  'giant-turtle': 'pet_turtle',
  // Humanoids
  'bandit': 'pirate_seaman_blue',
  'bandit-captain': 'pirate_mate',
  'thug': 'pirate_seaman_red',
  'guard': 'guardsman',
  'veteran': 'armored',
  'knight': 'knight',
  'noble': 'highborn',
  'commoner': 'peasant_male_a',
  'priest': 'priest',
  'acolyte': 'monk',
  'cultist': 'necromancer_side',
  'cult-fanatic': 'necromancer',
  'mage': 'wizard',
  'archmage': 'wizard',
  'druid': 'seeder',
  'scout': 'marksman',
  'spy': 'highborn',
  'berserker': 'general',
  'gladiator': 'general',
  'assassin': 'knight_dark',
  'tribal-warrior': 'spearman',
  // Giants / Large threats
  'ogre': 'troll_battle',
  'hill-giant': 'troll_battle',
  'stone-giant': 'troll_battle',
  'ettin': 'troll_battle',
  'troll': 'troll',
  // Dragons
  'young-red-dragon': 'dragon',
  'adult-red-dragon': 'dragon',
  'ancient-red-dragon': 'dragon',
  'red-dragon-wyrmling': 'dragon_hatchling',
  'young-green-dragon': 'dragon',
  'adult-green-dragon': 'dragon',
  'green-dragon-wyrmling': 'dragon_hatchling',
  'young-blue-dragon': 'dragon',
  'adult-blue-dragon': 'dragon',
  'blue-dragon-wyrmling': 'dragon_hatchling',
  'young-black-dragon': 'dragon',
  'adult-black-dragon': 'dragon',
  'black-dragon-wyrmling': 'dragon_hatchling',
  'young-white-dragon': 'dragon',
  'adult-white-dragon': 'dragon',
  'white-dragon-wyrmling': 'dragon_hatchling',
  'young-gold-dragon': 'dragon',
  'adult-gold-dragon': 'dragon',
  'pseudodragon': 'dragon_hatchling',
  'dragon-turtle': 'dragon',
  'wyvern': 'dragon',
  // Oozes
  'gray-ooze': 'slime',
  'grey-ooze': 'slime',
  'ochre-jelly': 'slime',
  'black-pudding': 'slime_tall',
  'gelatinous-cube': 'slime_cube',
  'slime': 'slime',
  // Fiends / Demons
  'imp': 'grin_purple',
  'quasit': 'grin_purple',
  'demon': 'grin_red',
  'succubus': 'necromancer',
  // Plants / Fey
  'awakened-tree': 'treant',
  'treant': 'treant',
  'dryad': 'seeder',
  'shambling-mound': 'mushroom_treant',
  'myconid-adult': 'mushroom',
  'myconid-sprout': 'mushroom_party',
  'violet-fungus': 'mushroom_infectous',
  'vine-blight': 'dionaea',
  'needle-blight': 'venus',
  'twig-blight': 'plant_leaf',
  // Elementals
  'fire-elemental': 'elemental_fire_1',
  'water-elemental': 'elemental_water_1',
  'air-elemental': 'elemental_air_1',
  'earth-elemental': 'elemental_earth_1',
  'magma-mephit': 'elemental_fire_1',
  'steam-mephit': 'elemental_air_1',
  'mud-mephit': 'elemental_earth_1',
  'ice-mephit': 'frozen_icer',
  // Monstrosities / misc
  'harpy': 'harpy',
  'manticore': 'harpy',
  'griffon': 'birds_phoenix',
  'hippogriff': 'birds_phoenix',
  'centaur': 'tales_aslan',
  'minotaur': 'troll_battle',
  // Constructs
  'animated-armor': 'armored',
  'flying-sword': 'armored',
  'shield-guardian': 'golem_blue',
  'helmed-horror': 'knight_dark',
  'stone-golem': 'golem_yellow',
  'iron-golem': 'golem_red',
  'clay-golem': 'golem_green',
  'flesh-golem': 'golem_blue',
};

// Fallback by Open5e creature type.
export const PACK_BY_TYPE = {
  dragon: 'dragon_hatchling',
  undead: 'skeleton',
  ooze: 'slime',
  beast: 'wolf_brown',
  fiend: 'grin_purple',
  fey: 'seeder',
  humanoid: 'peasant_male_a',
  construct: 'golem_blue',
  elemental: 'elemental_fire_1',
  plant: 'treant',
  monstrosity: 'troll',
  giant: 'troll_battle',
  aberration: 'grin_purple',
  celestial: 'priest',
};

// Name keyword matches — ordered specific → general. First hit wins.
export const PACK_BY_KEYWORD = [
  { test: /\bskeleton\s+archer\b/i, pack: 'skeleton_archer' },
  { test: /\bskeleton\s+(mage|caster)\b/i, pack: 'skeleton_mage' },
  { test: /\bskeleton\b/i, pack: 'skeleton' },
  { test: /\bzombie\b/i, pack: 'skeleton' },
  { test: /\bwight|wraith|specter|spectre|ghost|shade|shadow\b/i, pack: 'ghost' },
  { test: /\blich\b/i, pack: 'necromancer' },
  { test: /\bdire\s+wolf\b/i, pack: 'wolf_raider' },
  { test: /\bwolf\b/i, pack: 'wolf_brown' },
  { test: /\bdog|hound|mastiff\b/i, pack: 'labrador' },
  { test: /\bcat|panther|tiger|lion|jaguar|leopard\b/i, pack: 'feline_mutated_panther' },
  { test: /\bbear\b/i, pack: 'bear' },
  { test: /\bboar|pig\b/i, pack: 'boar' },
  { test: /\bhorse|steed|destrier\b/i, pack: 'farm_horse_adult' },
  { test: /\bdeer|stag|elk\b/i, pack: 'deer_elk' },
  { test: /\beagle|hawk|falcon|vulture\b/i, pack: 'birds_eagle_bald' },
  { test: /\bowl\b/i, pack: 'birds_owl_great_horned' },
  { test: /\bphoenix\b/i, pack: 'birds_phoenix' },
  { test: /\braven|crow\b/i, pack: 'birds_crow' },
  { test: /\brat\b/i, pack: 'dungeon_rat' },
  { test: /\bbat\b/i, pack: 'dungeon_bat' },
  { test: /\bspider\b/i, pack: 'spider' },
  { test: /\bscorpion\b/i, pack: 'frozen_scorpion' },
  { test: /\bbee\b/i, pack: 'bee' },
  { test: /\bbeetle\b/i, pack: 'insect_beetle' },
  { test: /\bfly|gnat\b/i, pack: 'insect_fly' },
  { test: /\bfirefly\b/i, pack: 'insect_firefly' },
  { test: /\bdragonfly\b/i, pack: 'insect_dragonfly' },
  { test: /\bsnake|viper|serpent|asp\b/i, pack: 'snake_viper' },
  { test: /\bconstrictor|python|anaconda\b/i, pack: 'snake_alien' },
  { test: /\bshark\b/i, pack: 'aquatic_shark_thresher' },
  { test: /\boctopus\b/i, pack: 'pet_octopus' },
  { test: /\bwhale\b/i, pack: 'pet_whale' },
  { test: /\bturtle|tortoise\b/i, pack: 'pet_turtle' },
  { test: /\bfrog|toad\b/i, pack: 'venus' },
  { test: /\bharpy\b/i, pack: 'harpy' },
  { test: /\bgoblin\b/i, pack: 'goblin_warrior' },
  { test: /\bhobgoblin\b/i, pack: 'goblin_spear' },
  { test: /\bbugbear\b/i, pack: 'troll' },
  { test: /\bkobold\b/i, pack: 'goblin_warrior' },
  { test: /\borc\b/i, pack: 'troll_battle' },
  { test: /\bogre|giant\b/i, pack: 'troll_battle' },
  { test: /\btroll\b/i, pack: 'troll' },
  { test: /\bdragon\b/i, pack: 'dragon' },
  { test: /\bwyvern|wyrm|drake\b/i, pack: 'dragon' },
  { test: /\bslime|ooze|jelly|pudding\b/i, pack: 'slime' },
  { test: /\bcube\b/i, pack: 'slime_cube' },
  { test: /\bmushroom|myconid|fungus\b/i, pack: 'mushroom' },
  { test: /\btreant|tree\b/i, pack: 'treant' },
  { test: /\bdryad|nymph|satyr\b/i, pack: 'seeder' },
  { test: /\bblight|vine|thorn\b/i, pack: 'dionaea' },
  { test: /\bfire\s+elemental|salamander|efreeti\b/i, pack: 'elemental_fire_1' },
  { test: /\bwater\s+elemental|marid\b/i, pack: 'elemental_water_1' },
  { test: /\bair\s+elemental|djinni\b/i, pack: 'elemental_air_1' },
  { test: /\bearth\s+elemental|dao\b/i, pack: 'elemental_earth_1' },
  { test: /\bmephit\b/i, pack: 'elemental_fire_1' },
  { test: /\bimp|quasit|demon|devil|fiend\b/i, pack: 'grin_red' },
  { test: /\bknight|paladin\b/i, pack: 'knight' },
  { test: /\bguard|soldier|warrior\b/i, pack: 'guardsman' },
  { test: /\barcher|marksman|ranger\b/i, pack: 'marksman' },
  { test: /\bmage|wizard|sorcerer|witch|warlock\b/i, pack: 'wizard' },
  { test: /\bpriest|cleric|monk\b/i, pack: 'priest' },
  { test: /\bcultist|acolyte\b/i, pack: 'necromancer_side' },
  { test: /\bnecromancer\b/i, pack: 'necromancer' },
  { test: /\bbandit|pirate|thug|brigand|raider\b/i, pack: 'pirate_seaman_blue' },
  { test: /\bnoble|lord|lady|aristocrat\b/i, pack: 'highborn' },
  { test: /\bcommoner|peasant|villager|townsperson\b/i, pack: 'peasant_male_a' },
  { test: /\bcentaur\b/i, pack: 'tales_aslan' },
  { test: /\bpenguin\b/i, pack: 'penguin' },
  { test: /\bsnowman\b/i, pack: 'snowman' },
  { test: /\bgolem|construct\b/i, pack: 'golem_blue' },
  { test: /\barmor\b/i, pack: 'armored' },
];

/**
 * Resolve a pack id for a monster.
 * Accepts either a full Open5e monster object ({ slug, name, type }) or a
 * lightweight { name, npcType } shape used by DOIP. Returns null if no match.
 */
export function monsterPackKey(monster) {
  if (!monster) return null;
  const slug = (monster.slug || '').toLowerCase();
  if (slug && PACK_BY_SLUG[slug] && MONSTER_PACKS[PACK_BY_SLUG[slug]]) return PACK_BY_SLUG[slug];
  const name = monster.name || '';
  if (name) {
    for (const { test, pack } of PACK_BY_KEYWORD) {
      if (test.test(name) && MONSTER_PACKS[pack]) return pack;
    }
  }
  const type = (monster.type || monster.npcType || '').toLowerCase();
  if (type && PACK_BY_TYPE[type] && MONSTER_PACKS[PACK_BY_TYPE[type]]) return PACK_BY_TYPE[type];
  return null;
}
